import React, { useLayoutEffect, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { mergeSx } from 'ui/merge-sx';

import ActionsDropdown from 'components/partials/actions-dropdown';
import Tooltip from 'components/partials/tooltip/tooltip';

import { useRegimeSelection } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-selection-context';

import Icons from 'assets/icons';

interface RegimeRowProps {
  regime: { id: string | number; name: string };
  actions: unknown[];
  dragListeners?: Record<string, unknown>;
  dragAttributes?: Record<string, unknown>;
  hideActions?: boolean;
  sx?: SxProps<Theme>;
}

const ROW_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  borderRadius: '0.5rem',
  pl: 2,
  pr: 1,
  py: 1,
};

const SELECTED_ROW_SX: SxProps<Theme> = { bgcolor: 'tokens.gray.100' };

const RegimeRow = ({
  regime,
  actions,
  dragListeners,
  dragAttributes,
  hideActions = false,
  sx,
}: RegimeRowProps): React.JSX.Element => {
  const regimeNameRef = useRef<HTMLButtonElement | null>(null);
  const { selectedRegimeId, onSelectRegime } = useRegimeSelection() as {
    selectedRegimeId?: string | number | null;
    onSelectRegime?: (id: string | number) => void;
  };
  const isSelected = selectedRegimeId === regime.id;
  const [isRegimeNameTruncated, setIsRegimeNameTruncated] =
    useState<boolean>(false);

  useLayoutEffect(() => {
    const element = regimeNameRef.current;

    if (element) {
      setIsRegimeNameTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [regime.name]);

  return (
    <Box
      sx={mergeSx(ROW_SX, isSelected ? SELECTED_ROW_SX : undefined, sx)}
      data-testid={`regime-row-${regime.id}`}
    >
      <Stack
        direction="row"
        sx={{
          flex: '1 1 0%',
          alignItems: 'center',
          gap: 1,
          pr: 1,
          minWidth: 0,
        }}
      >
        <button
          type="button"
          className="cursor-grab touch-none shrink-0"
          {...dragListeners}
          {...dragAttributes}
          aria-label="Drag to reorder"
        >
          <Icons.GripVertical customClass="size-4 text-gray-400" />
        </button>
        <Tooltip
          content={regime.name}
          enabled={isRegimeNameTruncated}
          position="top-start"
        >
          <button
            ref={regimeNameRef}
            type="button"
            className="text-sm text-gray-700 truncate max-w-36 text-left"
            onClick={() => onSelectRegime?.(regime.id)}
          >
            {regime.name}
          </button>
        </Tooltip>
      </Stack>
      {!hideActions && (
        <ActionsDropdown
          options={actions as never}
          variant="reserve-text"
          customClass="!h-auto !p-1"
        />
      )}
    </Box>
  );
};

export default RegimeRow;
